// Contratto pubblico dell'sdk violetta, cioè i tipi di `window.violetta` e dei
// payload dei suoi comandi.
//
// ATTENZIONE — questo file è una COPIA. La fonte di verità è
// `packages/shared/src/index.ts` del repository violetta (`interface Violetta`).
// Va tenuto allineato a mano: violetta non pubblica ancora un package npm con i
// propri tipi (`@violetta/shared` è `private`, senza build, con gli `exports` sui
// sorgenti `.ts` e `moduleResolution: bundler`), quindi da qui non è consumabile.
// Quando quel package esisterà, questo file va sostituito da un import.
//
// Nessun controllo automatico intercetta un disallineamento: un comando aggiunto
// là e non qui semplicemente non è chiamabile dagli store, e un payload cambiato
// là e non qui compila ma viene scartato a runtime dal parser dell'sdk.
//
// Le differenze deliberate rispetto all'originale sono tre, e nessuna tocca il
// contratto, che sono i nomi dei comandi e i tipi dei payload: i campi qui sono in
// ordine alfabetico perché lo impone `typescript-sort-keys`; i tipi sono dichiarati
// come `interface` invece che come `type`; e la coda dello stub `q` sta su
// un'interfaccia a parte (`Violetta extends ViolettaCommand`) invece che dentro
// l'unica interfaccia, perché qui serve un tipo dei soli comandi da dare a `command`.
//
// Questo modulo non deve importare altri moduli di b2x-react: è chiamato anche da
// AppContext.tsx (notifica del login), che a sua volta è importato da
// ViolettaRoot.tsx. Un import in uscita da qui creerebbe una dipendenza circolare.

// Config del pulsante di apertura, solo modalità widget.
export interface ViolettaButtonConfig {
  // Distanza dal bordo inferiore della viewport in px. Default: 24.
  bottom?: number;
  // Colore di sfondo del pulsante (qualsiasi valore CSS valido: hex, rgb, hsl…).
  // Attenzione: se valorizzato, l'sdk ignora del tutto il tema del trigger
  // per-tenant (background e foreground), non solo il colore di sfondo.
  color?: string;
  // Distanza dal bordo destro della viewport in px. Default: 24.
  right?: number;
  // Diametro del pulsante in px. Default: 56.
  size?: number;
}

// Config del pannello chat, solo modalità widget.
export interface ViolettaPanelConfig {
  // Altezza del pannello in px. Default: 600.
  height?: number;
  // Larghezza del pannello in px. Default: 400.
  width?: number;
}

// Campi comuni a entrambe le modalità di integrazione.
export interface ViolettaBaseConfig {
  // API-Key dello storefront-api del brand che incorpora violetta. Violetta la
  // inoltra come header API-Key alle chiamate storefront-api, senza conservarla.
  // È anche l'identità del tenant: guida il tema e il nome dell'assistente.
  // ATTENZIONE: viaggia nel browser (URL dell'iframe), quindi deve essere una
  // key PUBBLICA/RISTRETTA ai soli endpoint chat AI, MAI la key segreta a pieni
  // privilegi.
  apiKey?: string;
  // Codice lingua BCP-47 (es. "it", "en", "en-US"). Passato come searchParam
  // all'iframe: localizza la chat in ssr, quindi il primo paint è già nella
  // lingua giusta. La variante regionale viene normalizzata sulla lingua
  // supportata più vicina ("en-US" → "en"); una lingua non supportata ricade sul
  // default di violetta ("it").
  locale?: string;
  // Mode iniziale della chat: lo decide l'host, che è l'unico a sapere se la
  // propria pagina è chiara o scura. Default: "light".
  mode?: 'light' | 'dark';
  // Token firmato (HMAC o JWT short-lived) che identifica l'utente autenticato,
  // verificato da violetta lato server. È il canale della sessione INIZIALE, non
  // del login a runtime: la init è idempotente, quindi un token che arrivasse
  // dopo la prima non verrebbe applicato (l'sdk la scarta con un warn, e conta
  // l'inizializzazione, non il montaggio: una init che non ha montato niente
  // perché la pagina è esclusa, o perché ospita un embed, è comunque quella che
  // vale). Per il login a runtime si usa il comando "login" (vedi
  // `violetta.updateUser` più sotto).
  // Non passare mai dati sensibili in chiaro: solo un token firmato.
  //
  // Oggi nessuno store lo valorizza, e non c'è un campo in `appConfig.violetta`
  // che lo possa fare: `appConfig` è statico e dichiarato prima del render,
  // mentre un token firmato è per sessione e a scadenza breve. Il canale, quando
  // servirà, è una callback risolta prima della init, non un campo di config.
  token?: string;
}

// Opzioni della sola superficie widget. Non estende `ViolettaBaseConfig`, ed è la
// differenza che conta rispetto al vecchio `ViolettaWidgetConfig`: i campi comuni
// si dichiarano una volta sola al primo livello della init e valgono anche per gli
// embed della pagina, così le due superfici non possono divergere (prima potevano,
// perché ciascuna portava la propria copia di apiKey, token, locale e mode).
export interface ViolettaWidgetOptions {
  button?: ViolettaButtonConfig;
  // Se true, il pannello chat si apre subito dopo l'inizializzazione.
  defaultOpen?: boolean;
  // Pathname (o array di pathname) su cui il widget non deve montarsi, con
  // matching ESATTO su window.location.pathname, a meno dello slash finale che
  // l'sdk normalizza ("/chat" copre anche "/chat/"). Va scritto come il pathname
  // APPARE nella barra degli indirizzi, quindi con il basename del router se
  // c'è: `getPagePath` restituisce un path relativo al basename, e passarlo
  // grezzo non matcha mai su uno store multi-locale. Vedi ViolettaRoot.tsx, che
  // lo risolve con `useHref`.
  // L'sdk osserva da sé la navigazione spa (patch di pushState/replaceState +
  // popstate) e smonta o rimonta il widget di conseguenza, anche se la pagina di
  // ingresso era quella esclusa: nessun bisogno di gestirlo lato React.
  //
  // NON serve più a far convivere widget ed embed: il widget non si monta
  // comunque su una pagina dove è montato un embed, e l'sdk se ne accorge da sé
  // anche navigando. Qui resta per una ragione sola, cosmetica: senza, il
  // trigger comparirebbe per un istante navigando VERSO la pagina dell'embed,
  // perché il "mountEmbed" arriva solo dopo il render del componente che ospita
  // il container.
  excludedPaths?: Array<string> | string;
  panel?: ViolettaPanelConfig;
  // Z-index base: il pannello usa questo valore, il pulsante base+1.
  // Default: 2147483646 (massimo del browser meno 1).
  zIndex?: number;
}

// Payload del comando "init": i campi comuni più le superfici da montare.
// L'embed non è una chiave di questo oggetto e non ha un tipo di opzioni proprio:
// si monta con "mountEmbed", perché il suo container è un nodo del dom che in una
// spa esiste solo dopo il primo render. Il widget invece vive in <body>, che c'è
// sempre, e non ha un momento di montaggio da scegliere.
export interface ViolettaConfig extends ViolettaBaseConfig {
  // Widget floating: `true` per i default, un oggetto per personalizzarlo,
  // assente per non montarlo affatto. Non c'è fallback automatico.
  widget?: ViolettaWidgetOptions | boolean;
}

// Comandi di `window.violetta`, con un overload per comando: è ciò che dà
// autocomplete sul nome del comando e controllo sul payload.
export interface ViolettaCommand {
  // Configurazione dell'integrazione, UNA volta sola per pagina: dichiara i campi
  // comuni alle due superfici e monta il widget, se richiesto. Una seconda
  // chiamata viene ignorata con un warn, e l'ancora è l'inizializzazione, non il
  // montaggio. In b2x-react la chiama solo ViolettaRoot.tsx, che l'App monta una
  // volta sola e non smonta mai: nessun altro punto deve chiamarla.
  (cmd: 'init', config: ViolettaConfig): void;
  // Monta l'iframe embed nell'elemento con questo id, che deve essere già nel dom
  // quando il comando parte (garantito, la chiamata è in un useEffect).
  // Idempotente PER CONTAINER: se quel container ospita già un iframe violetta la
  // chiamata non fa nulla, quindi un effect rieseguito non duplica l'iframe e non
  // fa ripartire la conversazione.
  // Non porta configurazione: apiKey, token, locale e mode arrivano dalla "init".
  // Si può chiamare PRIMA della "init" (in React gli effect dei figli girano
  // prima di quelli dei padri): l'sdk lo tiene in coda e lo esegue appena la
  // configurazione arriva.
  (cmd: 'mountEmbed', containerId: string): void;
  (cmd: 'openWidget'): void;
  (cmd: 'closeWidget'): void;
  // Inverte lo stato corrente: apre se chiuso, chiude se aperto.
  (cmd: 'toggleWidget'): void;
  // Registra un handler per eventi in arrivo dall'iframe violetta. Si può
  // chiamare prima della "init" per non perdere eventi.
  // Non esiste un comando "off": gli handler si accumulano e non si rimuovono,
  // quindi la registrazione va fatta una volta sola (guard a livello di modulo,
  // non una ref: vedi ViolettaRoot.tsx) e l'handler deve leggere i valori
  // correnti da un riferimento esterno, non dalla closure.
  (cmd: 'on', event: 'ready' | 'pong', handler: () => void): void;
  (cmd: 'on', event: 'userMessage', handler: (payload: { text: string }) => void): void;
  // L'utente ha premuto "aggiungi al carrello" su una card prodotto in chat. Il
  // payload porta lo `skuId`, cioè la variante da aggiungere e non il prodotto.
  // Tocca all'host chiamare le proprie api e rispondere con "addToCartResult":
  // senza risposta la card resta in attesa fino al proprio timeout (10 secondi).
  (cmd: 'on', event: 'addToCart', handler: (payload: { skuId: string }) => void): void;
  // Eventi locali dell'sdk (solo modalità widget): apertura/chiusura del pannello.
  (cmd: 'on', event: 'widgetOpen' | 'widgetClose', handler: () => void): void;
  // Verifica della connessione con l'iframe: risponde con l'evento "pong".
  (cmd: 'send', event: 'ping'): void;
  // Login dell'utente a runtime. Passa solo l'idCustomer: violetta risolve il
  // nome per il saluto dal proprio backend, non dal payload.
  (cmd: 'send', event: 'login', payload: { idCustomer: string }): void;
  // Logout: chiude la conversazione in corso e torna allo stato anonimo.
  (cmd: 'send', event: 'logout'): void;
  // Cambia a runtime il mode della chat già montata senza rimontarla: aggiorna
  // anche il trigger del widget nel dom host in autonomia.
  (cmd: 'send', event: 'setThemeMode', payload: { mode: 'light' | 'dark' }): void;
  // Cambia a runtime la lingua della chat già montata senza rimontarla (la
  // conversazione in corso resta). Nei siti b2x-react non serve: il cambio
  // lingua è un hard refresh, quindi la lingua nuova è già nell'init successiva.
  (cmd: 'send', event: 'setLocale', payload: { locale: string }): void;
  // Esito dell'aggiunta al carrello chiesta con l'evento "addToCart": sblocca la
  // card che l'ha chiesta, con la conferma o con l'invito a riprovare. Lo
  // `skuId` va rimandato identico a quello ricevuto, è la chiave con cui la card
  // riconosce la risposta come propria. Va inviato in entrambi gli esiti.
  (cmd: 'send', event: 'addToCartResult', payload: { ok: boolean; skuId: string }): void;
}

// Interfaccia pubblica di `window.violetta`: i comandi più la coda dello stub.
export interface Violetta extends ViolettaCommand {
  // Coda dei comandi arrivati prima del bundle: il bootstrap dell'sdk la
  // rilegge come `[comando, ...argomenti]` e la riesegue nell'ordine.
  q?: Array<[string, ...Array<unknown>]>;
}

declare global {
  interface Window {
    // Opzionale, a differenza del contratto originale: qui lo script può non
    // essere caricato affatto (`violetta.enableWidget`/`enableEmbed` a false),
    // quindi la firma deve dirlo. I call site passano da `violetta.command`, che
    // installa lo stub e rende irrilevante il caso "non ancora caricato".
    violetta?: Violetta;
  }
}

// Firma larga dell'sdk, per l'inoltro interno. Gli overload di `ViolettaCommand`
// servono a chi chiama, che conosce il comando e quindi il proprio payload; qui
// il comando è una variabile e gli argomenti sono già stati validati dagli
// overload sul confine pubblico, quindi serve solo poterli inoltrare invariati.
type ViolettaCommandForward = (command: string, ...args: Array<unknown>) => void;

// Equivalente in codice dello stub dello snippet ufficiale: finché il bundle
// dell'sdk non è caricato i comandi si accumulano in `window.violetta.q`, e il
// bootstrap dell'sdk li riesegue nell'ordine originale. Tutti i call site devono
// passare da qui, mai da `window.violetta` diretto: così l'ordine fra i comandi
// e il caricamento dell'sdk non conta.
const command: ViolettaCommand = (cmd: string, ...args: Array<unknown>) => {
  if (!window.violetta) {
    const stub: Violetta = (queuedCommand: string, ...queuedArgs: Array<unknown>) => {
      stub.q = stub.q ?? [];
      stub.q.push([queuedCommand, ...queuedArgs]);
    };
    window.violetta = stub;
  }

  (window.violetta as ViolettaCommandForward)(cmd, ...args);
};

// Ultima identità comunicata a violetta, `undefined` per l'utente anonimo.
// Vive nel modulo e non in uno stato React perché i due chiamanti non sono nello
// stesso albero: la scrive AppContext (a ogni transizione di sessione) e la
// rilegge l'handler "ready" di Violetta.tsx, che è registrato una volta sola e
// quindi non può leggere una closure.
let lastIdCustomer: string | undefined;

// Comunica a violetta chi è l'utente corrente. Va chiamata sullo STATO della
// sessione e non su un evento di login, perché i casi da coprire sono più del
// login attivo: autologin, logout, e soprattutto il boot con una sessione già
// valida (chi ricarica la pagina da autenticato non passa da alcun login).
//
// L'identità è il solo idCustomer: violetta risolve il nome per il saluto dal
// proprio backend, non da questo payload.
//
// Il confronto con l'ultimo id inviato non è un'ottimizzazione ed è obbligatorio:
// setSession è chiamata anche a ogni aggiunta al carrello e a ogni update della
// wishlist, e ogni "login" costa a violetta una chiamata al proprio backend.
//
// L'invio può precedere la init, e in quel caso l'sdk non lo scarta più: lo mette
// in coda e lo esegue allo scodamento. Non cambia la sostanza — allo scodamento
// l'iframe è appena stato creato e il suo documento non ha ancora montato il
// listener, quindi il messaggio si perde comunque. La consegna vera resta
// l'handler "ready" con `resendUser`. Il comando parte lo stesso perché è invece
// indispensabile per il login a runtime, quando la chat è già montata e nessun
// "ready" arriverà più.
const updateUser = (idCustomer?: string) => {
  if (idCustomer === lastIdCustomer) {
    return;
  }

  // Nessun "logout" per chi era già anonimo: l'assenza di identità è lo stato
  // iniziale della chat, non una transizione da comunicare.
  const wasLoggedIn = lastIdCustomer !== undefined;
  lastIdCustomer = idCustomer;

  if (idCustomer !== undefined) {
    command('send', 'login', { idCustomer });
  } else if (wasLoggedIn) {
    command('send', 'logout');
  }
};

// Rimanda l'identità corrente a un iframe appena pronto, scavalcando la memoria
// dell'ultimo invio: l'utente non è cambiato, ma l'iframe è nuovo e non lo sa.
const resendUser = () => {
  if (lastIdCustomer !== undefined) {
    command('send', 'login', { idCustomer: lastIdCustomer });
  }
};

// Unica superficie del modulo, esposta come namespace per la stessa ragione di
// `baim` (analytics/baim.ts): in AppContext le notifiche ai servizi esterni si
// leggono in colonna, una riga per servizio, `baim.updateUser` accanto a
// `violetta.updateUser`.
//
// Le tre funzioni restano dichiarate fuori e qui solo referenziate: scritte inline
// nell'oggetto, `command` perderebbe i propri overload e servirebbe annotare a mano
// il tipo del namespace, come fa baim.
export const violetta = {
  command,
  resendUser,
  updateUser,
};
